import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import type { RegistryInvoice, RegistryInvoiceStatus } from "@/types/api";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { formatDate, formatMoney, formatPeriod } from "@/lib/billing-format";
import { cn } from "@/lib/utils";
import { InvoiceRowActions } from "@/components/billing-ops/invoice-row-actions";
import { CreditNoteAction } from "@/components/billing-ops/credit-note-dialog";

const STATUS_VARIANT: Record<RegistryInvoiceStatus, "success" | "secondary" | "muted" | "destructive"> = {
  paid: "success",
  partially_refunded: "secondary",
  refunded: "muted",
  open: "secondary",
  void: "muted",
  uncollectible: "destructive",
};

function InvoiceStatus({ invoice }: { invoice: RegistryInvoice }) {
  const { t } = useTranslation("billing");
  const overdue = invoice.status === "open" && invoice.overdueDays !== null;
  return (
    <div className="space-y-1">
      <Badge variant={overdue ? "destructive" : STATUS_VARIANT[invoice.status]}>
        {t(`payments.status.${overdue ? "overdue" : invoice.status}`)}
      </Badge>
      {overdue ? (
        <span className="block text-xs font-medium text-destructive">
          {t("payments.overdueDays", { count: invoice.overdueDays ?? 0 })}
        </span>
      ) : null}
      {invoice.paidOutOfBand ? (
        <span className="block text-xs text-muted-foreground">{t("payments.outOfBand")}</span>
      ) : null}
    </div>
  );
}

/** Credited / refunded amounts under the gross, when money went back. */
function ReturnedAmounts({ invoice }: { invoice: RegistryInvoice }) {
  const { t } = useTranslation("billing");
  if (!invoice.creditedCents && !invoice.refundedCents) return null;
  return (
    <span className="block text-xs font-normal text-muted-foreground">
      {invoice.creditedCents ? (
        <span className="block">
          {t("payments.credited", { amount: formatMoney(invoice.creditedCents, invoice.currency) })}
        </span>
      ) : null}
      {invoice.refundedCents ? (
        <span className="block">
          {t("payments.refunded", { amount: formatMoney(invoice.refundedCents, invoice.currency) })}
        </span>
      ) : null}
    </span>
  );
}

function InvoiceLinks({ invoice }: { invoice: RegistryInvoice }) {
  const { t } = useTranslation("billing");
  if (!invoice.invoicePdfUrl && !invoice.hostedInvoiceUrl && !invoice.creditNotePdfUrl) {
    return <span className="text-muted-foreground">—</span>;
  }
  return (
    <div className="flex flex-wrap gap-x-3">
      {invoice.invoicePdfUrl ? (
        <a
          className="text-primary hover:underline"
          href={invoice.invoicePdfUrl}
          target="_blank"
          rel="noopener noreferrer"
        >
          {t("payments.invoicePdf")}
        </a>
      ) : null}
      {invoice.hostedInvoiceUrl ? (
        <a
          className="text-primary hover:underline"
          href={invoice.hostedInvoiceUrl}
          target="_blank"
          rel="noopener noreferrer"
        >
          {t("payments.hostedInvoice")}
        </a>
      ) : null}
      {invoice.creditNotePdfUrl ? (
        <a
          className="text-primary hover:underline"
          href={invoice.creditNotePdfUrl}
          target="_blank"
          rel="noopener noreferrer"
        >
          {t("payments.creditNotePdf")}
        </a>
      ) : null}
    </div>
  );
}

export function InvoicesTable({ items }: { items: RegistryInvoice[] }) {
  const { t } = useTranslation("billing");
  return (
    <div className="overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>{t("payments.columns.number")}</TableHead>
            <TableHead>{t("payments.columns.instance")}</TableHead>
            <TableHead>{t("payments.columns.date")}</TableHead>
            <TableHead>{t("payments.columns.period")}</TableHead>
            <TableHead className="text-right">{t("payments.columns.gross")}</TableHead>
            <TableHead>{t("payments.columns.status")}</TableHead>
            <TableHead>{t("payments.columns.links")}</TableHead>
            <TableHead>{t("ops.actionsColumn")}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {items.length === 0 ? (
            <TableRow>
              <TableCell colSpan={8} className="py-6 text-center text-sm text-muted-foreground">
                {t("payments.empty")}
              </TableCell>
            </TableRow>
          ) : (
            items.map((inv) => (
              <TableRow
                key={inv.id}
                className={cn(inv.status === "open" && inv.overdueDays !== null && "bg-destructive/5")}
              >
                <TableCell className="font-mono text-xs">
                  {inv.invoiceNumber ?? inv.stripeInvoiceId}
                  {inv.kind ? (
                    <span className="block font-sans text-muted-foreground">
                      {t(`payments.kind.${inv.kind}`)}
                    </span>
                  ) : null}
                </TableCell>
                <TableCell>
                  <Link
                    to={`/instances/${inv.instanceName}`}
                    className="font-mono text-xs text-primary hover:underline"
                  >
                    {inv.instanceName}
                  </Link>
                </TableCell>
                <TableCell className="whitespace-nowrap text-muted-foreground">
                  {formatDate(inv.createdAt)}
                </TableCell>
                <TableCell className="whitespace-nowrap text-muted-foreground">
                  {formatPeriod(inv.periodStart, inv.periodEnd)}
                </TableCell>
                <TableCell className="whitespace-nowrap text-right font-medium tabular-nums">
                  {formatMoney(inv.totalCents, inv.currency)}
                  <ReturnedAmounts invoice={inv} />
                </TableCell>
                <TableCell>
                  <InvoiceStatus invoice={inv} />
                </TableCell>
                <TableCell>
                  <InvoiceLinks invoice={inv} />
                </TableCell>
                <TableCell>
                  {inv.status === "open" ? (
                    <InvoiceRowActions
                      stripeInvoiceId={inv.stripeInvoiceId}
                      instanceName={inv.instanceName}
                      label={inv.invoiceNumber ?? inv.stripeInvoiceId}
                      lastReminderAt={inv.lastReminderAt}
                    />
                  ) : inv.stripeInvoiceId.startsWith("in_") &&
                    (inv.status === "paid" || inv.status === "partially_refunded") ? (
                    <CreditNoteAction
                      stripeInvoiceId={inv.stripeInvoiceId}
                      instanceName={inv.instanceName}
                      label={inv.invoiceNumber ?? inv.stripeInvoiceId}
                    />
                  ) : null}
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </div>
  );
}
